/**
 * CrmProposalsView — the Proposals tab: recent proposals with type + status + fee, status filter
 * pills, search. Rows open CrmProposalDetailModal (computed fee breakdown). Authoring lands in 6b.
 */
import { useEffect, useRef, useState } from 'react'
import { getProposals } from '../../services/proposals.js'
import CrmProposalDetailModal from './CrmProposalDetailModal.jsx'
import { syncOpen } from './crmSync.js'

const STATUSES = ['All', 'Draft', 'Posted', 'Executed', 'Declined', 'Expired']
const STATUS_COLOR = { Draft: '#8a8f98', Posted: '#7c3aed', Executed: '#1f9d57', Declined: '#b3261e', Expired: '#9a6700', Cancelled: '#8a8f98' }
const TYPE_ABBR = { 'Basic Services': 'BS', 'Design Consultation': 'DC', 'ASR Proposal': 'ASR' }

function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '—' }

export default function CrmProposalsView() {
  const [props, setProps] = useState(null)
  const [status, setStatus] = useState('All')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(null)
  const loadedRef = useRef(false)

  useEffect(() => { if (!loadedRef.current) { loadedRef.current = true; getProposals().then(setProps).catch(() => setProps([])) } }, [])

  const list = Array.isArray(props) ? props : []
  const norm = s => String(s || '').toLowerCase()
  const filtered = list
    .filter(p => status === 'All' ? true : norm(p.status).includes(norm(status)))
    .filter(p => !q.trim() || `${p.name} ${p.projectName} ${p.clientName}`.toLowerCase().includes(q.toLowerCase()))

  function feeLabel(p) {
    if (p.type === 'Design Consultation') return money(p.retainer || p.contractFee)
    if (p.tiered && p.packages.C) return `${money(p.packages.A)}–${money(p.packages.C)}`
    return money(p.contractFee || p.packages.A)
  }

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <div style={S.pills}>
          {STATUSES.map(s => <button key={s} style={pill(status === s)} onClick={() => setStatus(s)}>{s}</button>)}
        </div>
        <div style={{ flex: 1 }} />
        <input style={S.search} placeholder="Search proposals…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div style={S.count}>{props === null ? 'Loading…' : `${filtered.length} proposal${filtered.length === 1 ? '' : 's'}`}</div>

      <div style={S.list}>
        {props === null ? null : filtered.length === 0 ? (
          <div style={S.empty}>No proposals{status !== 'All' ? ` (${status})` : ''}.</div>
        ) : filtered.map(p => (
          <button key={p.id} style={S.row} onClick={() => setOpen(p)}>
            <span style={S.typeTag}>{TYPE_ABBR[p.type] || '—'}</span>
            <span style={S.nameCol}>
              <span style={S.name}>{p.projectName || p.name || p.clientName || 'Proposal'}</span>
              {p.clientName && <span style={S.sub}>{p.clientName}</span>}
            </span>
            <span style={{ ...S.statusBadge, background: (STATUS_COLOR[p.status] || '#5a7884') + '1a', color: STATUS_COLOR[p.status] || '#5a7884' }}>{p.status}</span>
            <span style={S.fee}>{feeLabel(p)}</span>
          </button>
        ))}
      </div>

      {open && <CrmProposalDetailModal proposal={open} onClose={() => setOpen(null)}
        onChanged={() => getProposals().then(list => { setProps(list); setOpen(syncOpen(list)) }).catch(() => {})} />}
    </div>
  )
}

function pill(on) {
  return { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: on ? 600 : 400, color: on ? '#fff' : 'var(--aj-text-mid)', background: on ? '#07272D' : 'var(--aj-bg-muted)', border: 'none', borderRadius: 20, padding: '5px 13px', cursor: 'pointer' }
}

const S = {
  wrap: { maxWidth: 980, margin: '0 auto', padding: '18px 20px 60px' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  search: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 11px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, background: 'var(--aj-bg-elevated)', width: 200 },
  count: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', margin: '2px 2px 8px' },
  list: { display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer', textAlign: 'left', width: '100%' },
  typeTag: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 600, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '3px 6px', width: 34, textAlign: 'center', flexShrink: 0 },
  nameCol: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  name: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  statusBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px', flexShrink: 0 },
  fee: { fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', width: 130, textAlign: 'right', flexShrink: 0 },
  empty: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '24px 14px', textAlign: 'center' },
}
